/* @flow */
import {createSelector} from 'reselect';
import {OrderedMap, Map, Set} from 'immutable';
import {extent} from 'd3-array';
import {pipe, prop, groupBy, map} from 'ramda';
import {scaleOrdinal} from 'd3-scale';
import {format} from 'date-fns';

import {getterFromKeyPath, toLower, decodeIndicatorId} from 'trustar/utils';
import {sortBy} from 'trustar/utils/utils.js';
import {reports, reportList, currentReportId} from 'modules/reports/getters.js';
import {findReportInState} from 'modules/reports/utils.js';
import {indicatorList} from 'modules/indicators/getters.js';
import {enclavesMap} from 'modules/enclave/getters.js';
import {summary} from 'modules/summaries/getters.js';
import {type SummaryType} from 'modules/summaries/types.js';
import {
  Graph,
  typeof CurrentGraph,
  removeNodes,
  Filters,
  calculateFiltered,
  filterNodesByTimeRange,
  getNodeTimes,
  filterCategories,
} from 'trustar/constellation/graph-utils.js';
import type {BreadcrumbRecordType} from './types';

/**
 * NOTE:
 * It is important to remain cognizant of the fact that we deal in both node ids
 * and report ids within constellation. A node id is the id of a node within our
 * neo4j graph database, while a report id is the id of a report in our postgres
 * database. Nodes that represent incident reports also have a `initialId`
 * property that contains the report id.
 */

/* Updated Getters
 * ======================================================================= */
export const graphs = getterFromKeyPath(['constellation', 'graphs']);

export const hasUserSeenTimeRangeExplanation = getterFromKeyPath(['constellation', 'hasUserSeenTimeRangeExplanation']);

export const currentGraphId = getterFromKeyPath(['constellation', 'currentGraphId']);

export const rootReportId = getterFromKeyPath(['constellation', 'rootNodeId']);

export const notes = getterFromKeyPath(['constellation', 'notes']);

export const notesById = (id: string) => createSelector(notes, notes => notes.get(id));

/**
 * A Set containing all IDs of currently selected nodes
 */
export const currentNodeIds = getterFromKeyPath(['constellationMeta', 'currentNodeIds']);

export const activeNote = getterFromKeyPath(['constellationMeta', 'activeNote']);

/**
 * A node that is currently being highlighted
 */
export const highlightedNode = getterFromKeyPath(['constellationMeta', 'highlightedNode']);

/**
 * A breadcrumb trail of previously visited nodes on the graph
 * Additional Selectors Further Down
 */
export const breadcrumbTrailRaw = getterFromKeyPath(['constellationMeta', 'breadcrumbTrail']);
export const currentBreadcrumbId = getterFromKeyPath(['constellationMeta', 'currentBreadcrumbId']);

/**
 * A Set containing the IDs of all nodes (graph nodes) that are currently known
 * to have annotations. We use this for determining a boolean for whether or not
 * a given node has nodes
 */
export const nodesWithAnnotations = createSelector(notes, notes => Set(notes.keys()));

export const hoveredNodeId = getterFromKeyPath(['constellationMeta', 'hoveredNodeId']);

export const options = getterFromKeyPath(['constellationMeta', 'options']);

export const getLabelType = createSelector(options, prop('labelType'));

export const loading = getterFromKeyPath(['constellationMeta', 'loading']);

export const explosionIds = getterFromKeyPath(['constellationMeta', 'explosionIds']);

/**
 * NOTE: Current graph will default to an empty graph if there is no
 * current graph. I.e. this will never resolve to a void value.
 */
export const currentGraph = createSelector(
  [graphs, currentGraphId],
  (graphs: Map<string, *>, id: string): Map<string, *> =>
    graphs.get(id, Graph()).get('currentGraph')
);

/**
 * The current graph with positioning data merged in to the OrderedMap of nodes
 * for display purposes. The nodePositions comes from the d3 force simulation
 * and will be empty until the first graph is cached
 *
 * NOTE: an annotations array DOES come down the wire with each graph node. So,
 * we could just look for the existence of that array without doing any mapping
 * logic. However, that value is not reactive. We simply use it to populate the
 * notes store initially. But if a user adds a note to an otherwise unannotated
 * node then that state wouldn't change, but the `hasAnnotations` value will.
 */
export const currentGraphWithPositions = createSelector(
  [graphs, currentGraphId, nodesWithAnnotations],
  (graphs: Map<string, typeof Graph>, id: string, annotatedNotes: Set<string>): CurrentGraph => {
    const graph = graphs.get(id, Graph());
    const currentGraph: CurrentGraph = graph.get('currentGraph');
    const {nodePositions} = graph.get('meta');
    return currentGraph
      .mergeDeepIn(['data', 'nodes'], nodePositions)
      .updateIn(
        ['data', 'nodes'],
        nodes => nodes.map(x => x.set('hasAnnotations', annotatedNotes.has(x.get('id')))) // See NOTE
      );
  },
);


export const currentGraphFilters = createSelector(
  currentGraph,
  (graph: CurrentGraph) => graph.get('filters')
);

export const currentGraphFilterText = createSelector(
  currentGraphFilters,
  (filters: typeof Filters) => filters.get('filterText')
);

export const showLabels = createSelector(options, prop('showLabels'));

export const currentFilterStatus = createSelector(
  currentGraph,
  (graph: CurrentGraph) =>
    Filters().get('inactiveFilters').size - graph.getIn(['filters', 'inactiveFilters']).size > 0
);

/**
 * NOTE: This was a quickly thrown together getter. It includes duplicate date
 * and merges two types of data only to have them separated in the component
 * that uses them. HIGHLY legacy. TODO: Refactor
 **/
export const graphFiltersWithEnclaves = createSelector(
  [enclavesMap],
  (enc: Map<*, *>) => {
    const enclaveFilters = enc.map(filter => {
      return {
        type: 'incident_report',
        enclaveId: filter.get('id'),
        iconUrl: filter.get('iconUrl'),
        label: filter.get('name'),
        name: filter.get('name'), // See NOTE
        color: filter.get('color'),
      };
    });
    return filterCategories.merge(enclaveFilters);
  },
);

export const graphTagFilters = createSelector(
  [currentGraph, enclavesMap],
  (graph: CurrentGraph, enclaves: Map<string, any>) => {
    return graph
      .getIn(['data', 'nodes'])
      .filter(node => node.get('type') === 'INDICATOR_TAG')
      .map(tag => {
        const color = enclaves.getIn([tag.get('enclaveId'), 'color'], 'yellow');
        return tag.set('color', color);
      });
  }
);

/**
 * The current display graph. I.e. the graph with all nodes filtered or removed
 * by the user taken out.
 *
 * NOTE: This getter seems to be a bottleneck. We may need to refactor.
 */
export const currentDisplayGraph = createSelector(
  currentGraphWithPositions,
  (graph: CurrentGraph): CurrentGraph => {
    const inactiveFilters = graph.getIn(['filters', 'inactiveFilters']);
    return removeNodes(
      filterNodesByTimeRange(graph, graph.get('timeRange')),
      graph.get('deletedNodes').merge(calculateFiltered(graph, inactiveFilters))
    );
  }
);

export const currentDetailsGraph = createSelector(
  currentGraph,
  (graph: CurrentGraph): CurrentGraph => {
    const inactiveFilters = graph.getIn(['filters', 'inactiveFilters']);
    return removeNodes(
      filterNodesByTimeRange(graph, graph.get('timeRange')),
      graph.get('deletedNodes').merge(calculateFiltered(graph, inactiveFilters))
    );
  }
);

// Just some quick colors
const color = scaleOrdinal([
  'red',
  'gree',
  'blue',
  'orange',
  'yellow',
  'gold',
  'teal',
  'maroon',
]);

/**
 * What the hell is this... amirite? We were getting some odd UI errors related
 * to mapping over undefined values when trying to display node enclaves. In
 * other words, although we couldnt' repro it seems likely that node.enclaves
 * was somehow set to an array of undefined. Thus the default value of enclave
 * placeholder was born \\\(۶•̀ᴗ•́)۶//／
 *
 * This just creats a plain object which can stand in for an enclave until the
 * enclaves are fully fetched.
 */
const createEnclavePlaceholder = ({id}) => ({
  id,
  color: color(id),
});

export const currentGraphWithEnclaves = createSelector(
  [currentDisplayGraph, enclavesMap],
  (graph: CurrentGraph, enc: Map<*, *>) => {
    return graph.updateIn(['data', 'nodes'], nodes => {
      return nodes.map(node => {
        return node.update(x => {
          const enclaveIds = x.get('enclaveGuids');
          const enclaves = enclaveIds
            ? enclaveIds.map(id => enc.get(id, createEnclavePlaceholder({id})))
            : null;
          return x.set('enclaves', enclaves);
        });
      });
    });
  },
);

/**
 * The graph display data (i.e. { nodes, edges }) to be displayed to the user.
 */
export const currentGraphData = createSelector(
  currentDisplayGraph,
  prop('data'),
);

export const currentDetailsGraphData = createSelector(
  currentDetailsGraph,
  prop('data'),
);

/* This shows us the filters that are applicable to a graph and the number of nodes
 * present per filter type (or system if node has system)
 */
export const applicableFilters = createSelector(
  currentGraph,
  (graph: CurrentGraph) => {
    const filteredByTimeAndDeleted = removeNodes(
      filterNodesByTimeRange(graph, graph.get('timeRange')),
      graph.get('deletedNodes')
    );
    return filteredByTimeAndDeleted.getIn(['data', 'nodes'])
      .reduce((graphFilters, n) => {

        const type = toLower(n.get('type'));
        const enclaveGuids = n.get('enclaveGuids');
        const tagNode = type === 'indicator_tag';
        const reportNode = type === 'incident_report';

        if (reportNode && enclaveGuids) {
          const enclaves = enclaveGuids
            .toMap()
            .mapKeys((k, v) => v)
            .map(guid => graphFilters.get(guid, 0) + 1);
          return graphFilters.merge(enclaves);
        } else if (!tagNode) {
          return graphFilters.update(type, 0, count => count + 1);
        } else {
          return graphFilters;
        }
      }, Map());

  }
);

/**
 *
 * More Breadcrumbs
 */
export const rootNode = createSelector(
  [currentGraphData, rootReportId],
  (constellation: Constellation, rootNodeId: string) => {
    if (constellation) {
      const nodes = constellation.get('nodes', OrderedMap());
      const rootNode = nodes.get(rootNodeId);
      return rootNode;
    }

    return Map();
  }
);
export const breadcrumbTrail = createSelector(
  [breadcrumbTrailRaw],
  (breadcrumbTrail: OrderedMap<any, BreadcrumbRecordType>) => {

    return breadcrumbTrail;
  }
);

const enrichBreadcrumb = (breadcrumb: BreadcrumbRecordType, summary: SummaryType) => {
  if (!summary || (summary.get('reportId') !== breadcrumb.get('id'))) return breadcrumb;

  const closedSourceName = summary && summary.getIn(['source', 'name'], null);

  return closedSourceName ? breadcrumb.set('source', closedSourceName) : breadcrumb;
};

/**
 * Add Closed Source Data to breadcrumbs (i.e. Source)
 */
export const breadcrumbTrailEnriched = createSelector(
  [breadcrumbTrail, summary],
  (breadcrumbTrail: OrderedMap<any, BreadcrumbRecordType>, summary: SummaryType) => {

    return breadcrumbTrail.map(breadcrumb => enrichBreadcrumb(breadcrumb, summary));
  }
);

/**
 * NOTE: extent and range are both [number,number]. The difference is that the
 * range will be equal to or a subset of the full extnet. This getter is for the
 * full extent, i.e. the oldest and newest timestamps of nodes in this graph.
 */
export const currentTimeExtent = createSelector(
  currentGraph,
  (graph: CurrentGraph): [number, number] => {

    const nodes = graph.getIn(['data', 'nodes']);
    return pipe(getNodeTimes, extent)(nodes);
  },
);

export const currentNodeTimes = createSelector(
  currentGraph,
  pipe(
    (x: CurrentGraph) => x.getIn(['data', 'nodes']),
    getNodeTimes,
  )
);

export const currentDailyHistogram = createSelector(
  currentGraph,
  pipe(
    (x: CurrentGraph) => x.getIn(['data', 'nodes']),
    getNodeTimes,
    groupBy(x => format(x, 'YYYY-MM-DD')),
    map(prop('length')),
  )
);

/**
 * The current time range is different than extent in that it is a display
 * property, not a direct property of the data. It represents the time range
 * currently being displayed in the graph, so it will be equal to or a subset of
 * the time extent (getter above this one).
 */
export const currentTimeRange = createSelector(
  currentGraph,
  prop('timeRange'),
);

/* Old Getters
 * ======================================================================= */

type Constellation = Map<string, Map<string, any>>;

/* TODO: Refactor below
 *
 * Some of these are still necessary but others have been refactored into the
 * graph getters above.
 * ======================================================================= */

/**
 * A Map of all currently selected node ids to their node Maps
 */
export const currentNodes = createSelector(
  [currentNodeIds, currentGraphData],
  (ids: Set<string>, constellation: Constellation): Map<string, *> =>
    ids
      .toMap()
      .filter((_, k) => constellation.getIn(['nodes', k])) // Basically we want a filter-map of ids to their nodes if they exist
      .map((_, k) => constellation.getIn(['nodes', k])),
);

/**
 * The id of the currently selected node. Can be null. Will also be null if more
 * than one node is selected.
 */
export const currentNodeId = createSelector(currentNodeIds, nodes => {
  if (!nodes || nodes.size !== 1) return null;
  return nodes.first();
});

/**
 * Same as above but for the actual node
 */
export const currentNode = createSelector(currentNodes, nodes => {
  if (!nodes || nodes.size !== 1) return null;
  return nodes.first();
});

export const currentDetailsNodes = createSelector(
  [currentNodeIds, currentDetailsGraphData],
  (ids: Set<string>, constellation: Constellation): Map<string, *> =>
    ids
      .toMap()
      .filter((_, k) => constellation.getIn(['nodes', k])) // Basically we want a filter-map of ids to their nodes if they exist
      .map((_, k) => constellation.getIn(['nodes', k])),
);

export const currentReport = createSelector(
  [currentNodeIds, reports],
  (ids: Set<string>, reports: Map<string, Map<string, *>>): ?Map<string, *> =>
    reports.getIn([ids.first(), 'data']),
);

/**
 * This is convoluted and could likely be refactored. Here's the logic:
 *
 * The id is equal to the currently selected node id (selection.first()) or the
 * current id in the URL bar. This is important because if there is no
 * constellation we still want to show the report the user has clicked.
 *
 * Once we have that id (which can be null) we try to grab that report from
 * first the list of reports and then the list of search items (reportList),
 * some of which are report pointers.
 * This whole process can return undefined which indicates that the report has
 * not yet been fetched.
 *
 * UPDATE: We now check the current constellation for nodes as well. The reason
 * for this: If there is a report without a constellation then we need to still
 * grab the ID in the url. However, if there is a constellation the current
 * selection will default to the report id so we don't need to worry about
 * constellation.
 */

export const currentReportOrListItem = createSelector(
  [currentGraphData, reports, reportList, currentNodeIds, currentReportId],
  (
    constellation: Constellation,
    reports: OrderedMap<any, any>,
    reportList: OrderedMap<any, any>,
    ids: Set<string>,
    currentReportId: string
  ): Map<any, any> => {

    const hasNodes = Boolean(constellation.get('nodes').size);

    let report = findReportInState(currentReportId, reports, reportList);

    if (report.isEmpty() && hasNodes) {
      return findReportInState(ids.first(), reports, reportList);
    }

    return report;
  },
);

export const currentDecodedIndicatorReportId = createSelector(
  [currentReportId],
  (
    currentReportId: string
  ): Map<any, any> => {
    const decodedId = decodeIndicatorId(currentReportId).foldOr(err => {
      console.error(`Could not decode ID - "${currentReportId}". This is probably normal. We call fromBase64 on everything, even malformed ids`);
      return '';
    });
    return decodedId;
  },
);

/**
 * NOTE: This logic mapping is fairly ugly.
 *
 * The issue this solves is that sometimes a report or other entity does not
 * have any correlation, so there are no "nodes" to annotate. However, we do
 * allow annotating individual entities so we want the user to be able to create
 * notes even if there are no correlations. By ensuring that node will be
 * defined and initialId is present we can accomplish this.
 */
export const annotationNode = createSelector(
  [currentNodes, currentReportOrListItem],
  (nodes, fallback) => {
    let node = nodes.first() || fallback;

    if (!node.get('initialId')) {
      node = node.set('initialId', node.get('id'));
    }

    return node;
  }
);

export const currentNoteSeq = createSelector(
  [notes, annotationNode],
  (notes, node) => notes.get(node.get('initialId'), OrderedMap())
    .valueSeq()
    .sort(sortBy('created', 'DESC'))
);

export const noteCount = createSelector(currentNoteSeq, seq => {
  return seq.count();
});

export const indicatorsInCurrentGraph = createSelector(
  currentGraphData,
  (constellation: Constellation): Map<*, *> =>
    constellation
      .get('nodes')
      .filter((node) => node.get('nodeType') === 'INDICATOR'),
);



// WEBPACK FOOTER //
// ./src/app/modules/constellation/getters.js